/* Blvck theme — global behaviour. No dependencies. */
(() => {
  const theme = (window.theme = window.theme || {});

  /* ---------- Utilities ---------- */
  theme.fetchSections = async (url, sectionIds) => {
    const target = new URL(url, location.origin);
    target.searchParams.set('sections', sectionIds.join(','));
    const res = await fetch(target);
    return res.json();
  };

  theme.parseHTML = (html) => new DOMParser().parseFromString(html, 'text/html');

  theme.formatMoney = (cents, format = theme.moneyFormat) => {
    const value = Number(cents) / 100;
    const fmt = (n, decimals, thousands = ',', decimal = '.') => {
      const [int, frac] = n.toFixed(decimals).split('.');
      return int.replace(/\B(?=(\d{3})+(?!\d))/g, thousands) + (frac ? decimal + frac : '');
    };
    return format.replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => {
      switch (key) {
        case 'amount_no_decimals': return fmt(value, 0);
        case 'amount_with_comma_separator': return fmt(value, 2, '.', ',');
        case 'amount_no_decimals_with_comma_separator': return fmt(value, 0, '.', ',');
        case 'amount_with_space_separator': return fmt(value, 2, ' ', ',');
        case 'amount_with_apostrophe_separator': return fmt(value, 2, "'", '.');
        default: return fmt(value, 2);
      }
    });
  };

  let toastTimer;
  theme.toast = (message) => {
    const el = document.querySelector('[data-toast]');
    if (!el) return;
    el.textContent = message;
    el.classList.add('is-visible');
    clearTimeout(toastTimer);
    toastTimer = setTimeout(() => el.classList.remove('is-visible'), 3500);
  };

  /* ---------- Dialogs: [data-open="id"] / [data-close] ---------- */
  theme.openDialog = (id, opener) => {
    const dialog = document.getElementById(id);
    if (!dialog || dialog.open) return;
    dialog._opener = opener || document.activeElement;
    dialog.showModal();
    // Focus the panel rather than its first button: no ring on the close icon after a click,
    // keyboard users still land inside the dialog and Tab to the controls.
    if (!dialog.hasAttribute('tabindex')) dialog.setAttribute('tabindex', '-1');
    dialog.focus({ preventScroll: true });
    dialog.dispatchEvent(new CustomEvent('dialog:open'));
    opener?.setAttribute('aria-expanded', 'true');
  };

  theme.closeDialog = (dialog) => {
    if (!dialog?.open) return;
    dialog.close();
  };

  document.addEventListener('click', (event) => {
    const opener = event.target.closest('[data-open]');
    if (opener) {
      event.preventDefault();
      // Close any open dialog first, so one drawer replaces another.
      document.querySelectorAll('dialog[open]').forEach((d) => d.id !== opener.dataset.open && d.close());
      theme.openDialog(opener.dataset.open, opener);
      return;
    }
    const closer = event.target.closest('[data-close]');
    if (closer) {
      theme.closeDialog(closer.closest('dialog'));
      return;
    }
    // Click on the backdrop (the dialog element itself) closes it.
    // Content always fills the dialog, so a hit on the element itself is the backdrop.
    if (event.target.matches('dialog[open]')) theme.closeDialog(event.target);
  });

  document.addEventListener('close', (event) => {
    const dialog = event.target;
    if (!(dialog instanceof HTMLDialogElement)) return;
    dialog._opener?.setAttribute('aria-expanded', 'false');
    if (dialog._opener?.isConnected) dialog._opener.focus({ preventScroll: true });
  }, true);

  /* ---------- Header: height variable + hide on scroll down ---------- */
  theme.lastPageScroll = 0;
  addEventListener('scroll', () => { theme.lastPageScroll = Date.now(); }, { passive: true });

  const header = document.querySelector('[data-header]');
  if (header && header.classList.contains('header--transparent') && !document.querySelector('main .banner--first, main .collection-banner')) {
    header.classList.remove('header--transparent');
    document.documentElement.classList.remove('has-transparent-header');
  }
  if (header) {
    const root = document.documentElement;
    const setHeight = () => root.style.setProperty('--header-height', `${header.offsetHeight}px`);
    new ResizeObserver(setHeight).observe(header);
    setHeight();

    const mode = header.dataset.sticky; // none | always | scroll-up
    root.classList.toggle('header-sticky', mode !== 'none');

    // Scroll-linked header: in "scroll-up" mode the header slides by exactly the distance scrolled
    // (no timed animation), and elements marked [data-under-header] (filter bar, product info) are
    // pinned to its bottom edge in the same frame — so they always move together, without jumps.
    let height = header.offsetHeight;
    // Scroll distance at which the header reaches the top of the screen (the announcement bar
    // above it scrolls away first). Hiding only starts after that, so nothing ever drifts apart.
    const wrapper = header.closest('.shopify-section') || header;
    let pinAt = 0;
    const measure = () => {
      height = header.offsetHeight;
      pinAt = 0;
      // Only count blocks in the page flow: apps inject SVG sprites, scripts and fixed widgets up here too.
      for (let el = wrapper.previousElementSibling; el; el = el.previousElementSibling) {
        if (!(el instanceof HTMLElement)) continue;
        const position = getComputedStyle(el).position;
        if (position === 'fixed' || position === 'absolute') continue;
        pinAt += el.offsetHeight || 0;
      }
      root.style.setProperty('--announcement-height', `${pinAt}px`);
    };
    new ResizeObserver(() => { measure(); apply(); }).observe(header);
    measure();
    const pinned = () => document.querySelectorAll('[data-under-header]');
    const effectiveY = () => Math.max(0, scrollY - pinAt);
    let lastY = effectiveY();
    let offset = 0; // how many px of the header are hidden (0..height)
    let ticking = false;
    let scrolled = null;
    let hidden = false;
    let lastOffset = -1;
    let lastVisible = -1;
    let lastChrome = -1;
    const apply = () => {
      if (mode === 'scroll-up' && offset !== lastOffset) {
        lastOffset = offset;
        header.style.transform = offset ? `translate3d(0, ${-offset}px, 0)` : '';
      }
      const visible = mode === 'none' ? 0 : height - offset;
      if (visible !== lastVisible) {
        lastVisible = visible;
        pinned().forEach((el) => { el.style.top = `${visible + (Number(el.dataset.underHeaderGap) || 0)}px`; });
      }
      const isHidden = offset >= height;
      if (isHidden !== hidden) {
        hidden = isHidden;
        header.classList.toggle('is-hidden', isHidden);
        root.classList.toggle('header-hidden', isHidden);
      }
      // How much of the screen the announcement bar and header cover right now, so pinned photos
      // can grow into the space the header leaves instead of leaving a gap below them.
      const chrome = mode === 'none'
        ? Math.max(0, pinAt + height - scrollY)
        : visible + Math.max(0, pinAt - scrollY);
      if (chrome !== lastChrome) {
        lastChrome = chrome;
        root.style.setProperty('--chrome-visible', `${chrome}px`);
      }
    };
    const onScroll = () => {
      const y = effectiveY();
      const isScrolled = scrollY > 10;
      if (isScrolled !== scrolled) { scrolled = isScrolled; header.classList.toggle('is-scrolled', isScrolled); }
      if (mode === 'scroll-up' && !document.querySelector('dialog[open]')) {
        const next = Math.min(height, Math.max(0, offset + (y - lastY)));
        // Near the very top the header is always fully shown.
        offset = y <= height ? Math.min(next, y) : next;
      }
      apply();
      lastY = y;
      ticking = false;
    };
    theme.refreshPinned = apply;
    apply();
    addEventListener('scroll', () => {
      if (!ticking) { requestAnimationFrame(onScroll); ticking = true; }
    }, { passive: true });

    // Skip hover effects under the cursor while the page scrolls (mouse/trackpad only).
    if (matchMedia('(hover: hover)').matches) {
      let scrollTimer;
      addEventListener('scroll', () => {
        if (!root.classList.contains('is-scrolling')) root.classList.add('is-scrolling');
        clearTimeout(scrollTimer);
        scrollTimer = setTimeout(() => root.classList.remove('is-scrolling'), 160);
      }, { passive: true });
    }
    onScroll();

    // Mega menus: open on hover (desktop), on click everywhere.
    header.querySelectorAll('[data-mega]').forEach((item) => {
      const details = item.querySelector('details');
      if (!details) return;
      let timer;
      item.addEventListener('mouseenter', () => { clearTimeout(timer); timer = setTimeout(() => { details.open = true; }, 80); });
      item.addEventListener('mouseleave', () => { clearTimeout(timer); timer = setTimeout(() => { details.open = false; }, 150); });
      // Mouse click on "Men", "Women"… goes to the category's All page; hover shows the photo menu.
      // Keyboard (Enter/Space) and touch still open the menu so it stays reachable.
      const summary = details.querySelector('summary');
      summary?.addEventListener('click', (e) => {
        const mouse = e.detail > 0 && matchMedia('(hover: hover) and (pointer: fine)').matches;
        if (mouse && summary.dataset.href) {
          e.preventDefault();
          location.href = summary.dataset.href;
        }
      });
      details.addEventListener('toggle', () => {
        if (details.open) header.querySelectorAll('[data-mega] details[open]').forEach((d) => d !== details && (d.open = false));
        header.classList.toggle('has-mega-open', !!header.querySelector('[data-mega] details[open]'));
      });
    });
    document.addEventListener('shopify:block:select', (e) => {
      const details = e.target.closest?.('[data-mega]')?.querySelector('details');
      if (details) details.open = true;
    });
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') header.querySelectorAll('[data-mega] details[open]').forEach((d) => { d.open = false; d.querySelector('summary').focus(); });
    });
  }

  /* ---------- Slider ---------- */
  class SliderComponent extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('.slider__track');
      this.prev = this.querySelector('.slider__nav--prev');
      this.next = this.querySelector('.slider__nav--next');
      this.progress = this.querySelector('.slider__progress');
      if (!this.track) return;
      this.prev?.addEventListener('click', () => this.go(-1));
      this.next?.addEventListener('click', () => this.go(1));
      this.track.addEventListener('scroll', () => this.update(), { passive: true });
      this.enableDrag();
      new ResizeObserver(() => this.update()).observe(this.track);
      if (this.dataset.autoplay) this.autoplay(Number(this.dataset.autoplay) * 1000);
    }
    go(direction) {
      const slide = this.track.firstElementChild;
      if (!slide) return;
      const step = slide.getBoundingClientRect().width + parseFloat(getComputedStyle(this.track).columnGap || 0);
      const perView = Math.max(1, Math.floor(this.track.clientWidth / step));
      const atEnd = this.track.scrollLeft + this.track.clientWidth >= this.track.scrollWidth - 4;
      const atStart = this.track.scrollLeft <= 4;
      if (direction > 0 && atEnd && this.dataset.loop) return this.track.scrollTo({ left: 0, behavior: 'smooth' });
      if (direction < 0 && atStart && this.dataset.loop) return this.track.scrollTo({ left: this.track.scrollWidth, behavior: 'smooth' });
      this.track.scrollBy({ left: direction * step * perView, behavior: 'smooth' });
    }
    update() {
      const { scrollLeft, scrollWidth, clientWidth } = this.track;
      const max = scrollWidth - clientWidth;
      if (this.prev) this.prev.disabled = !this.dataset.loop && scrollLeft <= 4;
      if (this.next) this.next.disabled = !this.dataset.loop && scrollLeft >= max - 4;
      if (this.progress) {
        const width = Math.min(100, (clientWidth / scrollWidth) * 100);
        this.progress.style.setProperty('--progress-width', `${width}%`);
        this.progress.style.setProperty('--progress', `${max > 0 ? (scrollLeft / max) * (100 / width - 1) * 100 : 0}%`);
        this.progress.hidden = max <= 4;
      }
    }
    // Click-and-drag for mouse users (touch devices use native swiping).
    enableDrag() {
      if (!matchMedia('(hover: hover) and (pointer: fine)').matches) return;
      let startX = 0;
      let startLeft = 0;
      let moved = false;
      let down = false;
      this.track.addEventListener('pointerdown', (e) => {
        if (e.pointerType !== 'mouse' || e.button !== 0 || e.target.closest('button, input, select')) return;
        down = true;
        moved = false;
        startX = e.clientX;
        startLeft = this.track.scrollLeft;
      });
      addEventListener('pointermove', (e) => {
        if (!down) return;
        const dx = e.clientX - startX;
        if (!moved && Math.abs(dx) < 6) return;
        moved = true;
        this.track.classList.add('is-dragging');
        this.track.scrollLeft = startLeft - dx;
      });
      addEventListener('pointerup', () => {
        if (!down) return;
        down = false;
        if (!moved) return;
        // Settle on the nearest card, then let the click that follows the drag be swallowed.
        const slide = this.track.firstElementChild;
        if (slide) {
          const step = slide.getBoundingClientRect().width + parseFloat(getComputedStyle(this.track).columnGap || 0);
          this.track.scrollTo({ left: Math.round(this.track.scrollLeft / step) * step, behavior: 'smooth' });
        }
        requestAnimationFrame(() => this.track.classList.remove('is-dragging'));
      });
      this.track.addEventListener('click', (e) => { if (moved) { e.preventDefault(); e.stopPropagation(); moved = false; } }, true);
      this.track.addEventListener('dragstart', (e) => e.preventDefault());
    }
    autoplay(ms) {
      if (matchMedia('(prefers-reduced-motion: reduce)').matches) return;
      // A programmatic scroll anywhere cancels the page's momentum scrolling on macOS/iOS,
      // so only rotate while this slider is on screen, not hovered, and the page has been still.
      let visible = true;
      let hovered = false;
      new IntersectionObserver(([entry]) => { visible = entry.isIntersecting; }).observe(this);
      this.addEventListener('pointerenter', () => { hovered = true; });
      this.addEventListener('pointerleave', () => { hovered = false; });
      this.addEventListener('focusin', () => { hovered = true; });
      this.addEventListener('focusout', () => { hovered = false; });
      setInterval(() => {
        if (!visible || hovered || document.hidden) return;
        if (Date.now() - theme.lastPageScroll < 1500) return;
        this.go(1);
      }, ms);
    }
  }
  customElements.define('slider-component', SliderComponent);

  /* ---------- Reveal on scroll ---------- */
  const revealObserver = 'IntersectionObserver' in window && document.body.classList.contains('has-reveal')
    ? new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-revealed');
            revealObserver.unobserve(entry.target);
          }
        });
      }, { rootMargin: '0px 0px -8% 0px' })
    : null;
  theme.observeReveal = (scope = document) => {
    scope.querySelectorAll('[data-reveal]:not(.is-revealed)').forEach((el) => {
      if (revealObserver) revealObserver.observe(el); else el.classList.add('is-revealed');
    });
  };
  theme.observeReveal();
  // Theme editor: re-run on section load.
  document.addEventListener('shopify:section:load', (e) => theme.observeReveal(e.target));

  /* ---------- Product card: swatches swap image, quick add ---------- */
  document.addEventListener('click', (event) => {
    const swatch = event.target.closest('[data-card-swatch]');
    if (swatch) {
      event.preventDefault();
      const card = swatch.closest('.product-card');
      const img = card?.querySelector('.product-card__image--primary');
      if (img && swatch.dataset.src) {
        img.srcset = swatch.dataset.srcset || '';
        img.src = swatch.dataset.src;
      }
      const link = card?.querySelector('.product-card__link');
      if (link && swatch.dataset.url) link.href = swatch.dataset.url;
      card?.querySelectorAll('[data-card-swatch]').forEach((s) => s.classList.toggle('is-active', s === swatch));
      return;
    }

    const toggle = event.target.closest('.quick-add__toggle');
    if (toggle) {
      event.preventDefault();
      const qa = toggle.closest('.quick-add');
      document.querySelectorAll('.quick-add.is-open').forEach((o) => o !== qa && o.classList.remove('is-open'));
      qa.classList.toggle('is-open');
      qa.querySelector('.quick-add__option:not([disabled])')?.focus({ preventScroll: true });
      return;
    }
    if (!event.target.closest('.quick-add')) {
      document.querySelectorAll('.quick-add.is-open').forEach((o) => o.classList.remove('is-open'));
    }
  });

  document.addEventListener('mouseleave', (event) => {
    if (event.target.classList?.contains('product-card')) event.target.querySelector('.quick-add.is-open')?.classList.remove('is-open');
  }, true);

  /* ---------- Quantity buttons ---------- */
  document.addEventListener('click', (event) => {
    const btn = event.target.closest('[data-qty]');
    if (!btn) return;
    const input = btn.closest('.quantity')?.querySelector('input');
    if (!input) return;
    const step = Number(input.step) || 1;
    const min = Number(input.min) || 0;
    const max = input.max ? Number(input.max) : Infinity;
    const next = Math.min(max, Math.max(min, Number(input.value) + (btn.dataset.qty === 'plus' ? step : -step)));
    if (next !== Number(input.value)) {
      input.value = next;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }
  });

  /* ---------- Localization selectors submit on change ---------- */
  document.addEventListener('change', (event) => {
    const select = event.target.closest('[data-localization-select]');
    if (select) select.form.submit();
  });

  /* ---------- Newsletter → Klaviyo list (same request as the old homepage form) ---------- */
  document.addEventListener('submit', async (event) => {
    const form = event.target;
    const wrap = form.closest?.('[data-klaviyo-signup]');
    if (!wrap) return;
    event.preventDefault();
    const email = form.querySelector('[name="contact[email]"]')?.value.trim();
    if (!email) return;
    const firstName = form.querySelector('[name="contact[first_name]"]')?.value.trim();
    const button = form.querySelector('button[type="submit"]');
    const error = form.querySelector('[data-newsletter-error]');
    const profile = { email, subscriptions: { email: { marketing: { consent: 'SUBSCRIBED' } } } };
    if (firstName) profile.first_name = firstName;
    button?.classList.add('is-loading');
    if (button) button.disabled = true;
    if (error) error.hidden = true;
    try {
      const res = await fetch(`https://a.klaviyo.com/client/subscriptions?company_id=${encodeURIComponent(wrap.dataset.company)}`, {
        method: 'POST',
        headers: { 'content-type': 'application/vnd.api+json', revision: '2026-04-15' },
        body: JSON.stringify({
          data: {
            type: 'subscription',
            attributes: { custom_source: wrap.dataset.source, profile: { data: { type: 'profile', attributes: profile } } },
            relationships: { list: { data: { type: 'list', id: wrap.dataset.list } } },
          },
        }),
      });
      if (!res.ok) throw new Error(await res.text());
      const done = document.createElement('p');
      done.className = 'newsletter-form__message';
      done.setAttribute('role', 'status');
      done.textContent = wrap.dataset.success;
      form.replaceChildren(done);
      document.dispatchEvent(new CustomEvent('newsletter:subscribed', { detail: { email } }));
    } catch (e) {
      if (error) { error.textContent = wrap.dataset.error; error.hidden = false; }
      button?.classList.remove('is-loading');
      if (button) button.disabled = false;
    }
  });

  /* ---------- First visit: switch to the visitor's own country and currency ---------- */
  // Asks Shopify which country the visitor is in and, once per browser, submits the
  // localization form with it. Any later choice made in the selector is kept.
  (() => {
    if (!theme.autoDetectCountry || window.Shopify?.designMode) return;
    const KEY = 'blvck:country-checked';
    try {
      if (localStorage.getItem(KEY)) return;
      localStorage.setItem(KEY, '1');
    } catch (e) { return; } // no storage: never risk a reload loop
    const run = async () => {
      try {
        const res = await fetch('/browsing_context_suggestions.json?country[enabled]=true');
        const detected = (await res.json())?.detected_values?.country?.handle;
        if (!detected || detected === theme.country) return;
        const select = document.querySelector('[data-localization-select][name="country_code"]');
        if (!select || ![...select.options].some((o) => o.value === detected)) return;
        select.value = detected;
        select.form.submit();
      } catch (e) { /* keep the current country */ }
    };
    ('requestIdleCallback' in window) ? requestIdleCallback(run, { timeout: 2000 }) : setTimeout(run, 1);
  })();

  /* ---------- Lazy-load the predictive search script on first focus ---------- */
  const loadOnce = (src) => {
    if (document.querySelector(`script[src="${src}"]`)) return;
    const s = document.createElement('script');
    s.src = src; s.defer = true;
    document.head.append(s);
  };
  document.addEventListener('focusin', (e) => {
    if (e.target.matches('[data-predictive-input]')) loadOnce(theme.assets?.search);
  });
  document.addEventListener('dialog:open', (e) => {
    if (e.target.id === 'SearchDrawer') loadOnce(theme.assets?.search);
  }, true);
})();
